import {
    findDifferences,
    transformDotNotationObject,
} from 'components/businessVerification/helpers/validate';
import { BusinessVerificationStage, DocumentRejection } from './BusinessVerificationV3Models';
import * as Yup from 'yup';
import { FormikErrors, FormikValues, yupToFormErrors } from 'formik';
import { hasBankingProduct } from './productUtils';
import { processedErrors } from 'components/businessVerification/helpers/ExitContinueButtons';
import { BeneficialPersonalOwner, FormValues, OwnerTreeStructure } from './schema';
type ValidationSchemas = Partial<{
    [key in BusinessVerificationStage]: any;
}>;

const operatingAddressRequiredMessages: Record<string, string> = {
    operatingAddressStreet: 'Please enter your business address',
    operatingAddressNumber: 'Please enter your business address',
    operatingAddressPostCode: 'Please enter your business postal code',
    operatingAddressCity: 'Please enter your business city',
    operatingAddressState: 'Please enter your business state',
    operatingAddressCountry: 'Please select an option',
};
const operatingAddressFields = Object.keys(operatingAddressRequiredMessages);

// If any operating address field has a value, all of them become required.
// A multi-field .when() can't be used here since the fields are mutually
// dependent on each other, which yup detects as a cyclic dependency.
function operatingAddressTest(this: Yup.TestContext, value: Record<string, any>) {
    const provided = operatingAddressFields.filter((key) => !!value?.[key]);
    if (provided.length > 0 && provided.length < operatingAddressFields.length) {
        const errors = operatingAddressFields
            .filter((key) => !value?.[key])
            .map((key) =>
                this.createError({
                    path: `${this.path}.${key}`,
                    message: operatingAddressRequiredMessages[key],
                })
            );
        return new Yup.ValidationError(errors);
    }
    return true;
}


const registrationInformationSchema = Yup.object({
    registrationInformation: Yup.object({
        entityName: Yup.string()
            .nullable()
            .required('Please enter a name')
            .max(200, 'Maximum 200 characters'),
        entityTypesID: Yup.number().nullable().required('Please select an option'),
        entityTypesOther: Yup.string().max(200, 'Maximum 200 characters').nullable(),
        registrationDate: Yup.mixed()
            .nullable()
            .required('Please enter the date of entity registration'),
        registrationNumber: Yup.string()
            .nullable()
            .required('Please enter a number')
            .max(200, 'Maximum 200 characters')
            .test('only-numbers', 'Please enter only numbers', (v) =>
                v == null || v === '' || /^\d+$/.test(String(v).trim())
            ),
        officialBusinessRegistrationNumber: Yup.string()
            .nullable()
            .required('Please enter a number')
            .max(200, 'Maximum 200 characters')
            .test('only-numbers', 'Please enter only numbers', (v) =>
                v == null || v === '' || /^\d+$/.test(String(v).trim())
            ),
        taxNumber: Yup.string()
            .nullable()
            .required('Please enter a number')
            .max(100, 'Maximum 100 characters'),
        doingBusinessAs: Yup.string()
            .nullable()
            .required("Please list all your business's names")
            .max(500, 'Maximum 500 characters'),
        addressStreet: Yup.string()
            .nullable()
            .required("Please enter your business's address")
            .max(200, 'Maximum 200 characters'),
        addressNumber: Yup.string()
            .nullable()
            .required("Please enter your business's address")
            .max(200, 'Maximum 200 characters'),
        addressPostCode: Yup.string()
            .nullable()
            .required("Please enter your business's postal code")
            .max(20, 'Maximum 20 characters'),
        addressCity: Yup.string()
            .nullable()
            .required("Please enter your business's city")
            .max(200, 'Maximum 200 characters'),
        addressState: Yup.string()
            .nullable()
            .required("Please enter your business's state")
            .max(100, 'Maximum 100 characters'),
        addressCountry: Yup.string()
            .nullable()
            .required('Please select an option')
            .max(10, 'Maximum 10 characters'),
        operatingAddressStreet: Yup.string()
            .nullable()
            .optional()
            .max(200, 'Maximum 200 characters'),
        operatingAddressNumber: Yup.string()
            .nullable()
            .optional()
            .max(200, 'Maximum 200 characters'),
        operatingAddressPostCode: Yup.string()
            .nullable()
            .optional()
            .max(20, 'Maximum 20 characters'),
        operatingAddressCity: Yup.string().nullable().optional().max(200, 'Maximum 200 characters'),
        operatingAddressState: Yup.string()
            .nullable()
            .optional()
            .max(100, 'Maximum 100 characters'),
        operatingAddressCountry: Yup.string()
            .nullable()
            .optional()
            .max(10, 'Maximum 10 characters'),
    }).test(
        'operatingPartialProvided',
        'Must Provide full operating address if started',
        function (this) {
            const {
                operatingAddressStreet,
                operatingAddressNumber,
                operatingAddressPostCode,
                operatingAddressCity,
                operatingAddressState,
                operatingAddressCountry,
            } = this.parent.registrationInformation as FormValues['registrationInformation'];

            const operationsAddress: Record<string, string | null> = {
                operatingAddressStreet,
                operatingAddressNumber,
                operatingAddressPostCode,
                operatingAddressCity,
                operatingAddressState,
                operatingAddressCountry,
            };

            if (
                Object.values(operationsAddress).some((val) => !!val) &&
                !Object.values(operationsAddress).every((val) => !!val)
            ) {
                // If any operating address value is given require the rest
                const firstEmptyValue = Object.keys(operationsAddress).find(
                    (opt) => !operationsAddress[opt]
                );
                return this.createError({
                    path: `${this.path}.${firstEmptyValue}`,
                    message: 'Please provide the full address if providing operating address.',
                });
            }

            return true;
        }
    ),
    // Licensing fields shown on Registration step but stored in operationsInformation
    operationsInformation: Yup.object({
        bLicenseRequired: Yup.string().nullable().required('Please select an option'),
        primaryRegulator: Yup.string()
            .nullable()
            .max(500, 'Maximum 500 characters')
            .when('bLicenseRequired', {
                is: 'Yes',
                then: (schema) => schema.required('Please enter your primary regulator'),
            }),
        licenseNumber: Yup.string()
            .nullable()
            .max(100, 'Maximum 100 characters')
            .when('bLicenseRequired', {
                is: 'Yes',
                then: (schema) => schema.required('Please enter your license number'),
            }),
        additionalLicensingInfo: Yup.string().nullable().max(500, 'Maximum 500 characters'),
    }),
});

const operationsInformationSchema = Yup.object({
    operationsInformation: Yup.object({
        webAddress: Yup.string()
            .nullable()
            .required('Website Address is required')
            .max(200, 'Maximum 200 characters'),
        phoneNumber: Yup.string()
            .nullable()
            .required('Business Phone Number is required')
            .max(100, 'Maximum 100 characters'),
        supportEmail: Yup.string()
            .nullable()
            .required('Support Email is required')
            .email('Please enter a valid email')
            .max(200, 'Maximum 200 characters'),
        bPubliclyListed: Yup.string().nullable().required('Please select an option'),
        ticker: Yup.string()
            .when('bPubliclyListed', {
                is: (bPubliclyListed: string) => bPubliclyListed === 'Yes',
                then: Yup.string()
                    .nullable()
                    .required("Please provide your business's ticker")
                    .max(500, 'Maximum 500 characters'),
            })
            .nullable(),
        exchanges: Yup.string()
            .when('bPubliclyListed', {
                is: (bPubliclyListed: string) => bPubliclyListed === 'Yes',
                then: Yup.string()
                    .nullable()
                    .required('Please provide the exchange(s) your business is listed on')
                    .max(500, 'Maximum 500 characters'),
            })
            .nullable(),
        operationRegionIds: Yup.array(Yup.number())
            .nullable()
            .min(1, 'Please select one or more options')
            .required('Please select one or more options'),
        bLicenseRequired: Yup.string().nullable().required('Please select an option'),
        additionalLicensingInfo: Yup.string().nullable().max(500, 'Maximum 500 characters'),
        primaryRegulator: Yup.string()
            .nullable()
            .max(500, 'Maximum 500 characters')
            .when('bLicenseRequired', {
                is: 'Yes',
                then: (schema) => schema.required('Please enter your primary regulator'),
            }),
        licenseNumber: Yup.string()
            .nullable()
            .max(100, 'Maximum 100 characters')
            .when('bLicenseRequired', {
                is: 'Yes',
                then: (schema) => schema.required('Please enter your license number'),
            }),
        businessActivityIds: Yup.array(Yup.number())
            .nullable()
            .min(1, 'Please select one or more options')
            .required('Please select one or more options'),
        web3ChainsAndAddresses: Yup.string().nullable().max(500, 'Maximum 500 characters'),
        sourceOfFundsIds: Yup.array(Yup.number())
            .nullable()
            .min(1, 'Please select one or more options')
            .required('Please select one or more options'),
        sourceOfFundsOther: Yup.string().nullable().max(200, 'Maximum 200 characters'),
        activeBanks: Yup.string()
            .nullable()
            .max(500, 'Maximum 500 characters')
            .when('$hasBankingProduct', {
                is: true,
                then: (schema) =>
                    schema
                        .required(
                            'Please enter the name of the bank for which the business holds an active account'
                        )
                        .test(
                            'active-banks-non-empty',
                            'Please enter the name of the bank for which the business holds an active account',
                            (v) => typeof v === 'string' && v.trim().length > 0
                        ),
                otherwise: (schema) => schema,
            }),
        yearlyTransactionsId: Yup.number().nullable(),
        bFundsReceivedUSA: Yup.boolean().nullable(),
        bFundsSentUSA: Yup.boolean().nullable(),
        purposeOfAccountId: Yup.number().nullable(),
        purposeOfAccountOther: Yup.string()
            .nullable()
            .required('Please enter the purpose of account')
            .max(200, 'Maximum 200 characters'),
        anticipatedMonthlyTransactionCountId: Yup.mixed()
            .nullable()
            .when('$hasBankingProduct', {
                is: true,
                then: (schema) =>
                    schema
                        .required('Please select anticipated monthly transaction count')
                        .test(
                            'anticipated-monthly-transaction-count-selected',
                            'Please select anticipated monthly transaction count',
                            (v) => v !== null && v !== undefined && String(v).trim() !== ''
                        ),
                otherwise: (schema) => schema,
            }),
        anticipatedMonthlyVolumeUsdId: Yup.mixed()
            .nullable()
            .when('$hasBankingProduct', {
                is: true,
                then: (schema) =>
                    schema
                        .required('Please select anticipated monthly volume (USD)')
                        .test(
                            'anticipated-monthly-volume-usd-selected',
                            'Please select anticipated monthly volume (USD)',
                            (v) => v !== null && v !== undefined && String(v).trim() !== ''
                        ),
                otherwise: (schema) => schema,
            }),
        countriesSendingPaymentsTo: Yup.string()
            .nullable()
            .max(500, 'Maximum 500 characters')
            .when('$hasBankingProduct', {
                is: true,
                then: (schema) =>
                    schema
                        .required('Please select countries from which funds will be sent')
                        .test(
                            'sending-countries',
                            'Please select at least one option',
                            (v) =>
                                typeof v === 'string' &&
                                v
                                    .split(',')
                                    .map((s) => s.trim())
                                    .filter(Boolean).length > 0
                        ),
                otherwise: (schema) => schema,
            }),
        countriesReceivingPaymentsFrom: Yup.string()
            .nullable()
            .max(500, 'Maximum 500 characters')
            .when('$hasBankingProduct', {
                is: true,
                then: (schema) =>
                    schema
                        .required('Please select countries from which funds will be received')
                        .test(
                            'receiving-countries',
                            'Please select at least one option',
                            (v) =>
                                typeof v === 'string' &&
                                v
                                    .split(',')
                                    .map((s) => s.trim())
                                    .filter(Boolean).length > 0
                        ),
                otherwise: (schema) => schema,
            }),

        // Crypto OTC fields
        blockchainAddresses: Yup.string().nullable().max(500, 'Maximum 500 characters'),
        industryType: Yup.string().nullable().max(200, 'Maximum 200 characters'),
        digitalAssetsTrading: Yup.string().nullable().max(200, 'Maximum 200 characters'),
        anticipatedMonthlyCryptoTransactionsId: Yup.number().nullable(),
        anticipatedMonthlyCryptoVolumeUsdId: Yup.number().nullable(),
        cryptoBusinessPlan: Yup.string().nullable().max(2000, 'Maximum 2000 characters'),

        // PORTL Questions (Crypto Acquiring) fields
        cryptoPaymentTypes: Yup.string().nullable().max(500, 'Maximum 500 characters'),
        integrationMethodId: Yup.number().nullable(),
        expectedMonthlyPaymentVolumeUsdId: Yup.number().nullable(),
        averageTransactionSize: Yup.string().nullable().max(100, 'Maximum 100 characters'),
        settlementPreferenceId: Yup.number().nullable(),

        // Merchant Services Questions fields
        bAcceptCardPayments: Yup.string().nullable(),
        customerTrafficCountries: Yup.mixed()
            .nullable()
            .test('maxLength', 'Maximum 500 characters', (val) => {
                if (val == null) return true;
                const str = Array.isArray(val) ? val.join(', ') : String(val);
                return str.length <= 500;
            }),
        historicalChargebackRate: Yup.string().nullable().max(50, 'Maximum 50 characters'),
        historicalRefundRate: Yup.string().nullable().max(50, 'Maximum 50 characters'),
        productsServicesSold: Yup.string().nullable().max(2000, 'Maximum 2000 characters'),
    }),
});

export function validateOperationsInformationStep(
    values: FormikValues,
    productServices: string | undefined,
    productList: Parameters<typeof hasBankingProduct>[1]
) {
    return operationsInformationSchema
        .validate(values, {
            abortEarly: false,
            context: { hasBankingProduct: hasBankingProduct(productServices, productList) },
        })
        .then(() => ({} as FormikErrors<FormValues>))
        .catch((e) => {
            if (e instanceof Yup.ValidationError) {
                return yupToFormErrors(e);
            }
            throw e;
        });
}

export const beneficialOwnersStructureSchema: Yup.AnySchema = Yup.object({
    ownersGuid: Yup.string(),
    bIsBusiness: Yup.boolean(),
    percentageOwned: Yup.number()
        .when('bControllingParty', {
            is: true,
            // Controlling parties aren't necessarily owners, so 0% is allowed.
            then: (schema) => schema.min(0, 'Percentage of shares owned must be 0 or greater'),
            otherwise: (schema) =>
                schema.moreThan(0, 'Percentage of shares owned must be greater than 0%'),
        })
        .max(100, 'Percentage of shares owned cannot exceed 100%')
        .required('Please provide a percentage of shares owned')
        .typeError('Please provide a percentage of shares owned')
        .nullable(),
    bControllingParty: Yup.boolean()
        .nullable()
        .when('bIsBusiness', {
            is: false,
            then: (schema) =>
                schema.required('Please state whether this party is a controlling party'),
        }),
    positionAtCompany: Yup.string()
        .trim()
        .nullable()
        .when('bIsBusiness', {
            is: false,
            then: (schema) =>
                schema
                    .required('Please provide a position at the company')
                    .max(100, 'Maximum 100 characters'),
        }),
    children: Yup.array(
        // as any because typescript complains otherwise. Even with typeof beneficialOwnersStructure
        Yup.lazy(() => beneficialOwnersStructureSchema.default(undefined)) as any
    ).nullable(),
});

const findSiblingGuids = (nodes: OwnerTreeStructure[] | null | undefined, targetGuid: string): string[] => {
    if (!Array.isArray(nodes)) return [];
    const nodeGuids = nodes.map((n) => n.ownersGuid);
    const result: string[] = [];
    if (nodeGuids.includes(targetGuid)) {
        result.push(...nodeGuids.filter((g) => g !== targetGuid));
    }
    for (const node of nodes) {
        result.push(...findSiblingGuids(node.children, targetGuid));
    }
    return result;
};

const individualBeneficialOwnersSchema = Yup.object({
    firstName: Yup.string()
        .trim()
        .max(100, 'Maximum 100 characters')
        .required('Please provide a first name'),
    lastName: Yup.string()
        .trim()
        .max(100, 'Maximum 100 characters')
        .required('Please provide a last name'),
    title: Yup.string().max(100, 'Maximum 100 characters').required('Please provide a title'),
    dateOfBirth: Yup.string().required('Please provide a date of birth').nullable(),
    nationality: Yup.string().required('Please provide a nationality'),
    occupationTypesId: Yup.number().nullable().required('Please select an occupation'),
    email: Yup.string()
        .max(200, 'Maximum 200 characters')
        .required('Please provide an email address')
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Please enter a valid email address')
        .test(
            'isRepeatedEmail',
            'Beneficial owners can not share the same email address',
            (value, context) => {
                const arrayParent = (context as any).from?.[1]?.value?.individualBeneficialOwners;
                const isEmailRepeated =
                    arrayParent.filter(
                        (beneficialOwner: BeneficialPersonalOwner) =>
                            beneficialOwner.email === value
                    ).length > 1;
                return !isEmailRepeated;
            }
        ),
    phoneNumber: Yup.string()
        .max(100, 'Maximum 100 characters')
        .required('Please provide a phone number'),
    country: Yup.string().required('Please provide a country'),
    ssn: Yup.string()
        .max(100, 'Maximum 100 characters')
        .required('Please provide a tax ID number/SSN')
        .test(
            'isDuplicateSsn',
            'This tax ID is already used by another beneficial owner at this level',
            (value, context) => {
                if (!value) return true;
                const normalize = (v: string) => v.replace(/[\s.\-]/g, '');
                const normalized = normalize(value);
                const currentOwner = (context as any).from?.[0]?.value;
                const ownerInfo = (context as any).from?.[1]?.value;
                if (!currentOwner?.guid || !ownerInfo) return true;
                const siblingGuids = findSiblingGuids(ownerInfo.beneficialOwnersStructure, currentOwner.guid);
                const hasDuplicateIndividual = Array.isArray(ownerInfo.individualBeneficialOwners) &&
                    ownerInfo.individualBeneficialOwners.some(
                        (o: BeneficialPersonalOwner) =>
                            siblingGuids.includes(o.guid) && o.ssn && normalize(o.ssn) === normalized
                    );
                const hasDuplicateBusiness = Array.isArray(ownerInfo.businessBeneficialOwners) &&
                    ownerInfo.businessBeneficialOwners.some(
                        (o: any) =>
                            siblingGuids.includes(o.guid) && o.taxNumber && normalize(o.taxNumber) === normalized
                    );
                return !hasDuplicateIndividual && !hasDuplicateBusiness;
            }
        ),
    stateProvince: Yup.string()
        .max(100, 'Maximum 100 characters')
        .required('Please provide a state/province'),
    city: Yup.string().max(200, 'Maximum 200 characters').required('Please provide a city'),
    addressLine1: Yup.string()
        .max(200, 'Maximum 200 characters')
        .required('Please provide a street address'),
    addressLine2: Yup.string().max(200, 'Maximum 200 characters'),
    postcode: Yup.string()
        .max(20, 'Maximum 20 characters')
        .required('Please provide a postal/zip code'),
    idDocumentFilename: Yup.string()
        .max(200, 'Maximum 200 characters')
        .required('Please provide an ID document')
        .nullable(),
    proofOfAddressFilename: Yup.string()
        .max(200, 'Maximum 200 characters')
        .required('Please provide a proof of address')
        .nullable(),
});
const businessBeneficialOwnerSchema = Yup.object({
    entityName: Yup.string()
        .max(200, 'Maximum 200 characters')
        .nullable()
        .required('Please enter a name'),
    entityTypesID: Yup.number().nullable().required('Please select an option'),
    entityTypesOther: Yup.string().max(200, 'Maximum 200 characters').nullable(),
    registrationDate: Yup.string()
        .nullable()
        .required('Please enter the date of entity registration'),
    registrationNumber: Yup.string()
        .transform((v) => (v == null || v === '' ? v : String(v)))
        .max(100, 'Maximum 100 characters')
        .nullable()
        .required('Please enter a registration number')
        .test('only-numbers', 'Please enter only numbers', (v) =>
            v == null || v === '' || /^\d+$/.test(String(v).trim())
        ),
    officialBusinessRegistrationNumber: Yup.string()
        .transform((v) => (v == null || v === '' ? v : String(v)))
        .max(200, 'Maximum 200 characters')
        .nullable()
        .required('Please enter a number')
        .test('only-numbers', 'Please enter only numbers', (v) =>
            v == null || v === '' || /^\d+$/.test(String(v).trim())
        ),
    taxNumber: Yup.string()
        .transform((v) => (v == null || v === '' ? v : String(v)))
        .max(100, 'Maximum 100 characters')
        .nullable()
        .required('Please enter a number')
        .test(
            'isDuplicateTaxNumber',
            'This tax ID is already used by another beneficial owner at this level',
            (value, context) => {
                if (!value) return true;
                const normalize = (v: string) => v.replace(/[\s.\-]/g, '');
                const normalized = normalize(value);
                const currentOwner = (context as any).from?.[0]?.value;
                const ownerInfo = (context as any).from?.[1]?.value;
                if (!currentOwner?.guid || !ownerInfo) return true;
                const siblingGuids = findSiblingGuids(ownerInfo.beneficialOwnersStructure, currentOwner.guid);
                const hasDuplicateBusiness = Array.isArray(ownerInfo.businessBeneficialOwners) &&
                    ownerInfo.businessBeneficialOwners.some(
                        (o: any) =>
                            siblingGuids.includes(o.guid) && o.taxNumber && normalize(o.taxNumber) === normalized
                    );
                const hasDuplicateIndividual = Array.isArray(ownerInfo.individualBeneficialOwners) &&
                    ownerInfo.individualBeneficialOwners.some(
                        (o: BeneficialPersonalOwner) =>
                            siblingGuids.includes(o.guid) && o.ssn && normalize(o.ssn) === normalized
                    );
                return !hasDuplicateBusiness && !hasDuplicateIndividual;
            }
        ),
    doingBusinessAs: Yup.string()
        .max(500, 'Maximum 500 characters')
        .nullable()
        .required("Please list all your business's names"),
    addressStreet: Yup.string()
        .max(200, 'Maximum 200 characters')
        .nullable()
        .required("Please enter your business's address"),
    addressNumber: Yup.string()
        .max(200, 'Maximum 200 characters')
        .nullable()
        .required("Please enter your business's address"),
    addressPostCode: Yup.string()
        .max(20, 'Maximum 20 characters')
        .nullable()
        .required("Please enter your business's postal code"),
    addressCity: Yup.string()
        .max(200, 'Maximum 200 characters')
        .nullable()
        .required("Please enter your business's city"),
    addressState: Yup.string()
        .max(100, 'Maximum 100 characters')
        .nullable()
        .required("Please enter your business's state"),
    addressCountry: Yup.string()
        .max(10, 'Maximum 10 characters')
        .nullable()
        .required('Please select an option'),
    operatingAddressStreet: Yup.string().max(200, 'Maximum 200 characters').nullable(),
    operatingAddressNumber: Yup.string().max(200, 'Maximum 200 characters').nullable(),
    operatingAddressPostCode: Yup.string().max(20, 'Maximum 20 characters').nullable(),
    operatingAddressCity: Yup.string().max(200, 'Maximum 200 characters').nullable(),
    operatingAddressState: Yup.string().max(100, 'Maximum 100 characters').nullable(),
    operatingAddressCountry: Yup.string().max(10, 'Maximum 10 characters').nullable(),
    businessFormationDocumentFilename: Yup.string()
        .max(200, 'Maximum 200 characters')
        .required('Please provide a Business Formation Document')
        .nullable(),
    companyUboCertificateOfGoodStandingFilename: Yup.string()
        .max(200, 'Maximum 200 characters')
        .required('Please provide a Company UBO Certificate of Good Standing')
        .nullable(),
    companyUboW9FormFilename: Yup.string()
        .max(200, 'Maximum 200 characters')
        .required('Please provide a Company UBO W9 Form (US)')
        .nullable(),
    proofOfAddressFilename: Yup.string()
        .max(200, 'Maximum 200 characters')
        .required('Please provide a proof of address')
        .nullable(),
}).test('operatingAddressPartiallyProvided', 'operatingAddressPartiallyProvided', operatingAddressTest);

const hasOwnControllingPartyAmongChildren = (nodes: any[]): boolean =>
    Array.isArray(nodes) &&
    nodes.some((child: any) => child.bIsBusiness === false && child.bControllingParty === true);

/** The root level ("Your Business" itself) needs its own immediate controlling party. */
const hasRootControllingParty = (nodes: any[]): boolean => {
    if (!Array.isArray(nodes) || nodes.length === 0) return true;
    return hasOwnControllingPartyAmongChildren(nodes);
};

/** Guids of businesses that appear in the tree with their own owners (children). */
const getBusinessGuidsWithChildren = (nodes: any[], guids = new Set<string>()): Set<string> => {
    (nodes ?? []).forEach((node) => {
        if (node.bIsBusiness && (node.children ?? []).length > 0) guids.add(node.ownersGuid);
        getBusinessGuidsWithChildren(node.children ?? [], guids);
    });
    return guids;
};

/** Every nested business needs a controlling party among its own children. */
const hasControllingParty = (
    nodes: any[],
    businessGuidsWithChildren: Set<string> = getBusinessGuidsWithChildren(nodes)
): boolean => {
    if (!Array.isArray(nodes)) return true;

    return nodes.every((node) => {
        // A business repeated elsewhere in the tree only lists its owners at its main
        // instance (see instanceAlreadyExistsWithChildren), so its controlling party is
        // checked there; this childless reference instance has no buttons to add one.
        const isReferenceInstance =
            node.bIsBusiness &&
            (node.children ?? []).length === 0 &&
            businessGuidsWithChildren.has(node.ownersGuid);

        // If node is a business, it must have an immediate individual controlling party child
        if (
            node.bIsBusiness &&
            !isReferenceInstance &&
            !hasOwnControllingPartyAmongChildren(node.children || [])
        ) {
            return false;
        }

        // Check the same rule recursively for all children
        return hasControllingParty(node.children || [], businessGuidsWithChildren);
    });
};

const OWNERSHIP_PERCENTAGE_TOLERANCE = 0.01;

const sumDirectBeneficialOwnerPercentages = (nodes: any[]): number =>
    (nodes ?? []).reduce((sum: number, node: any) => sum + (node?.percentageOwned ?? 0), 0);

/**
 * All ownership percentages at a level (including controlling parties) cannot exceed 100%.
 */
const validatePercentageSum = (nodes: any[]): boolean => {
    if (!Array.isArray(nodes) || nodes.length === 0) return true;

    if (sumDirectBeneficialOwnerPercentages(nodes) - 100 > OWNERSHIP_PERCENTAGE_TOLERANCE) {
        return false;
    }

    return nodes.every((node) => validatePercentageSum(node.children || []));
};


const ownerInformationSchema = Yup.object({
    ownerInformation: Yup.object({
        exemptionId: Yup.number().nullable(),
        individualBeneficialOwners: Yup.array(individualBeneficialOwnersSchema),
        businessBeneficialOwners: Yup.array(businessBeneficialOwnerSchema),
        // Beneficial owners are ALWAYS required, regardless of any Exempt List
        // selection. The exemption is optional and does not waive the requirement.
        beneficialOwnersStructure: Yup.array()
            .of(beneficialOwnersStructureSchema)
            .transform((value) => (value === null ? [] : value))
            .required('Please add at least one beneficial owner')
            .min(1, 'Please add at least one beneficial owner')
            .test(
                'has-root-controlling-party',
                'Please add a controlling party for your business',
                function (this: any, value: any) {
                    const context = this;
                    if (!hasRootControllingParty(value || [])) {
                        return context.createError({
                            path: `${context.path}`,
                            message: 'Please add a controlling party for your business',
                        });
                    }
                    return true;
                }
            )
            .test(
                'has-controlling-party',
                'At least one controlling party must be selected',
                function (this: any, value: any) {
                    const context = this;
                    if (!hasControllingParty(value || [])) {
                        return context.createError({
                            path: `${context.path}`,
                            message: 'All business owners must have a controlling party',
                        });
                    }
                    return true;
                }
            )
            .test(
                'direct-owners-percentage-max',
                'The combined ownership percentage cannot exceed 100% at each business level',
                function (this: any, value: any) {
                    if (!validatePercentageSum(value || [])) {
                        return this.createError({
                            path: this.path,
                            message:
                                'The combined ownership percentage cannot exceed 100% at each business level',
                        });
                    }
                    return true;
                }
            ),

        // Always required — owners are mandatory in all cases, so the confirmation
        // must be ticked regardless of any Exempt List selection.
        bConfirmAboveTen: Yup.boolean()
            .nullable()
            .required('Please confirm beneficial owners have 10% or more ownership')
            .oneOf([true], 'Please confirm beneficial owners have 10% or more ownership'),

        authorizedSigner: Yup.string()
            .nullable()
            .required('Please name an authorized signer')
            .max(200, 'Maximum 200 characters'),
    }),
});

const termsSchema = Yup.object({
    terms: Yup.object({
        bTaxAcknowledgement: Yup.boolean()
            .nullable()
            .required('Please agree to the tax acknowledgement')
            .oneOf([true], 'Please agree to the tax acknowledgement'),
        // FATCA is required unless this is a DDA-only product selection (handled via Yup context)
        bFatcaAcknowledgement: Yup.boolean()
            .nullable()
            .when('$hasDDAOnly', {
                is: true,
                // DDA-only: FATCA not required
                then: (schema) => schema,
                // Non-DDA-only (DDA + others, or no DDA): FATCA must be agreed
                otherwise: (schema) =>
                    schema
                        .required('Please confirm FATCA acknowledgement')
                        .oneOf([true], 'Please confirm FATCA acknowledgement'),
            }),
        bConfirmTrueAndCorrect: Yup.boolean()
            .nullable()
            .required('Please confirm the information you have provided is true and correct')
            .oneOf([true], 'Please confirm the information you have provided is true and correct'),
        bAcceptTerms: Yup.boolean()
            .nullable()
            .required('Please accept our terms of service')
            .oneOf([true], 'Please accept our terms of service'),
        // Only rendered/required for the jhglobal brand — FinalSteps defaults this to true
        // for other brands so it doesn't block their Continue button.
        bServiceFeeAcknowledgement: Yup.boolean()
            .nullable()
            .required('Please review and accept the Service Fee Terms & Conditions')
            .oneOf([true], 'Please review and accept the Service Fee Terms & Conditions'),
        // bEConsentDisclosure: Yup.boolean()
        //     .nullable()
        //     .required('You must agree to the E-Consent Disclosure')
        //     .oneOf([true], 'You must agree to the E-Consent Disclosure'),
        // bDepositAccountAgreement: Yup.boolean()
        //     .nullable()
        //     .required('You must agree to the Deposit Account Agreement')
        //     .oneOf([true], 'You must agree to the Deposit Account Agreement'),
        // bUSAPatriotActNotice: Yup.boolean()
        //     .nullable()
        //     .required('You must read and acknowledge the USA PATRIOT Act Notice.')
        //     .oneOf([true], 'You must read and acknowledge the USA PATRIOT Act Notice.'),
    }),
    // accountCurrencySelection: Yup.number()
    //     .nullable()
    //     .required('Please select the type of account you are looking for')
    //     .oneOf([0, 1], 'Please select a valid account type'),
});
export const documentUploadValidationSchema = Yup.object().shape({
    documentsUpload: Yup.array().of(
        Yup.object().shape({
            documentTypesID: Yup.number().nullable().required(),
            fileName: Yup.string()
                .when('bAlwaysRequired', {
                    is: (bAlwaysRequired: boolean) => bAlwaysRequired,
                    then: Yup.string().nullable().required('Please provide the requested document'),
                })
                .nullable(),
            bAlwaysRequired: Yup.boolean().nullable(),
        })
    ),
});

export const verificationValidationSchema: ValidationSchemas = {
    [BusinessVerificationStage.RegistrationInformation]: registrationInformationSchema,
    [BusinessVerificationStage.OperationsInformation]: operationsInformationSchema,
    [BusinessVerificationStage.OwnerInformation]: ownerInformationSchema,
    [BusinessVerificationStage.Terms]: termsSchema,
    [BusinessVerificationStage.Documents]: documentUploadValidationSchema,
};

export const validateOwners = async (values: FormikValues) => {
    await ownerInformationSchema.validate(values, { abortEarly: false }).catch((err) => {
        return err;
    });
};
export const validateDocuments = async (values: any) => {
    try {
        await documentUploadValidationSchema.validate(values, {
            abortEarly: false,
        });
        return {};
    } catch (err) {
        if (!(err instanceof Yup.ValidationError)) {
            return {};
        }

        const errors: any = {};

        err.inner.forEach((error) => {
            if (!error.path) return;

            const path = error.path.replace(/\[(\d+)\]/g, '.$1').split('.');

            let current = errors;

            path.forEach((key, index) => {
                if (index === path.length - 1) {
                    current[key] = error.message;
                } else {
                    current[key] = current[key] ?? {};
                    current = current[key];
                }
            });
        });

        return errors;
    }
};

export function validate(
    values: FormikValues,
    submitValues: FormValues | undefined,
    apiErrors: processedErrors,
    validationSchema: Yup.SchemaOf<any>,
    rejectedDocuments: DocumentRejection | null | undefined,
    context?: any
) {
    const handleRejected = (rejectedDocuments: DocumentRejection | null | undefined) => {
        let rejectedDocErrors: FormikErrors<any>[] = [];
        if (rejectedDocuments) {
            // TODO: Handle rejected documents
        }
        return rejectedDocErrors;
    };

    return new Promise(async function (resolve, reject) {
        const errorObject = await validationSchema
            .validate(values, { abortEarly: false, context })
            .then(() => {
                let rejectedDocErrors = handleRejected(rejectedDocuments);
                if (submitValues) {
                    let errors: FormikErrors<any> = {};
                    const diff = findDifferences(submitValues, values);
                    if (apiErrors.length > 0) {
                        apiErrors.forEach((error) => {
                            // Normalize bracket-notation array indices ([0]) to dot-notation (.0)
                            // so the comparison matches what findDifferences produces.
                            const normalizedFieldName = error.fieldName.replace(/\[(\d+)\]/g, '.$1');
                            if (
                                error.fieldName.endsWith('Other') &&
                                diff.some((diffField) =>
                                    diffField.startsWith(normalizedFieldName.slice(0, -5))
                                )
                            )
                                return; // skip fieldNameOther fields if fieldName... has changed
                            if (diff && (!diff.includes(normalizedFieldName) || diff.length === 0)) {
                                errors = { ...errors, [error.fieldName]: error.message };
                            }
                        });
                    }
                    if (rejectedDocErrors.length > 0) {
                        rejectedDocErrors.forEach((error) => {
                            errors = { ...errors, ...error };
                        });
                    }
                    if (Object.keys(errors) && Object.keys(errors).length > 0) {
                        return transformDotNotationObject(errors);
                    }
                }
            })
            .catch((e) => {
                if (submitValues) {
                    let rejectedDocErrors = handleRejected(rejectedDocuments);
                    if (e instanceof Yup.ValidationError) {
                        let errors: FormikErrors<any> = e.inner.reduce(
                            (acc: FormikErrors<any>, currentError) => {
                                const errorPath = currentError.path as string;
                                if (errorPath && !acc[errorPath]) {
                                    acc[errorPath] = currentError.message;
                                }
                                return acc;
                            },
                            {}
                        );
                        const diff = findDifferences(submitValues, values);
                        if (apiErrors.length > 0) {
                            apiErrors.forEach((error) => {
                                const normalizedFieldName = error.fieldName.replace(/\[(\d+)\]/g, '.$1');
                                if (
                                    diff &&
                                    (!diff.includes(normalizedFieldName) || diff.length === 0)
                                ) {
                                    errors = { ...errors, [error.fieldName]: error.message };
                                }
                            });
                        }
                        if (rejectedDocErrors.length > 0) {
                            rejectedDocErrors.forEach((error) => {
                                errors = { ...errors, ...error };
                            });
                        }
                        if (Object.keys(errors) && Object.keys(errors).length > 0) {
                            return transformDotNotationObject(errors);
                        }
                    }
                }
                if (e instanceof Yup.ValidationError)
                    return transformDotNotationObject(
                        e.inner.reduce((acc: FormikErrors<any>, currentError) => {
                            const errorPath = currentError.path as string;
                            if (errorPath && !acc[errorPath]) {
                                acc[errorPath] = currentError.message;
                            }
                            return acc;
                        }, {})
                    );
            });
        resolve(errorObject);
    });
}
